import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { cn } from "../../../utils";
const TITLE_CLASS = "mt-3 mb-2 text-base font-normal text-black";
const DESCRIPTION_CLASS = "text-sm text-muted-foreground";
// The input's `accept` only filters the file picker; a drop bypasses it, so
// both paths re-check every file against the same tokens.
function matchesAccept(file, accept) {
    const fileName = file.name.toLowerCase();
    const mimeType = file.type.toLowerCase();
    return accept.split(",").map((token)=>token.trim().toLowerCase()).some((token)=>{
        if (token.startsWith(".")) return fileName.endsWith(token);
        if (token.endsWith("/*")) return mimeType.startsWith(token.slice(0, -1));
        return mimeType === token;
    });
}
const listNames = (files)=>files.map((file)=>file.name).join(", ");
export function DropZone({ id, onChange, accept = ".xlsx,.xls", uploading = false, disabled = false, multiple = false, maxSize = 10, title, description, className, icon }) {
    const { t } = useTranslation();
    const inputRef = useRef(null);
    const [isDragging, setIsDragging] = useState(false);
    const handleFiles = (fileList)=>{
        if (!fileList) return;
        const accepted = [];
        const wrongType = [];
        const tooLarge = [];
        for (const file of Array.from(fileList)){
            if (!matchesAccept(file, accept)) wrongType.push(file);
            else if (file.size > maxSize * 1024 * 1024) tooLarge.push(file);
            else accepted.push(file);
        }
        if (wrongType.length > 0) {
            toast.error(t("common.dropzone.rejectedType", {
                names: listNames(wrongType)
            }));
        }
        if (tooLarge.length > 0) {
            toast.error(t("common.dropzone.rejectedSize", {
                names: listNames(tooLarge),
                maxSize
            }));
        }
        if (accepted.length > 0) {
            onChange(accepted);
        }
        // Reset input to allow re-selecting the same file
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };
    const handleDragOver = (e)=>{
        e.preventDefault();
        if (!disabled && !uploading) {
            setIsDragging(true);
        }
    };
    const handleDragEnter = (e)=>{
        e.preventDefault();
        if (!disabled && !uploading) {
            setIsDragging(true);
        }
    };
    const handleDragLeave = (e)=>{
        e.preventDefault();
        setIsDragging(false);
    };
    const handleDrop = (e)=>{
        e.preventDefault();
        setIsDragging(false);
        if (!disabled && !uploading) {
            handleFiles(e.dataTransfer.files);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "w-full",
        children: [
            /*#__PURE__*/ _jsx("label", {
                htmlFor: `dropzone-${id}`,
                onDragOver: handleDragOver,
                onDragEnter: handleDragEnter,
                onDragLeave: handleDragLeave,
                onDrop: handleDrop,
                className: cn("flex flex-col items-center justify-center w-full min-h-[174px] rounded-lg cursor-pointer transition-colors", "border-2 border-dashed border-line-strong", className, isDragging && "border-line-strong border-solid bg-surface-subtle", (disabled || uploading) && "opacity-50 pointer-events-none cursor-not-allowed"),
                children: !uploading ? /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col items-center justify-center py-6 px-4",
                    children: [
                        icon ?? /*#__PURE__*/ _jsx(Upload, {
                            className: "w-10 h-10 text-fg-subtle mb-3"
                        }),
                        typeof title === "string" ? /*#__PURE__*/ _jsx("p", {
                            className: TITLE_CLASS,
                            children: title
                        }) : title ?? /*#__PURE__*/ _jsxs("p", {
                            className: TITLE_CLASS,
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    children: [
                                        t("common.dropzone.dragAndDrop"),
                                        " "
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold",
                                    children: t("common.dropzone.clickToSelect")
                                })
                            ]
                        }),
                        typeof description === "string" ? /*#__PURE__*/ _jsx("p", {
                            className: DESCRIPTION_CLASS,
                            children: description
                        }) : description ?? /*#__PURE__*/ _jsx("p", {
                            className: DESCRIPTION_CLASS,
                            children: t("common.dropzone.maxFileSize", {
                                maxSize
                            })
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs("div", {
                    className: "flex flex-col items-center justify-center py-6",
                    children: [
                        /*#__PURE__*/ _jsx(Loader2, {
                            className: "w-10 h-10 text-fg-subtle animate-spin mb-3"
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "text-base font-medium text-fg-secondary",
                            children: t("common.uploadingEllipsis")
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("input", {
                ref: inputRef,
                id: `dropzone-${id}`,
                type: "file",
                className: "hidden",
                accept: accept,
                multiple: multiple,
                disabled: disabled || uploading,
                onChange: (e)=>handleFiles(e.target.files)
            })
        ]
    });
}

//# sourceMappingURL=drop-zone.js.map